import startCase from 'lodash/startCase';

import type {
  DataAuditEntityType,
  DataAuditSupportedFieldApi,
  DataAuditSupportedFieldRow,
} from 'src/components/data-audit/types';

import {SUPPORTED_FIELD_NONE_VALUE} from './constants';


function normalizeSupportedFieldText(value: string | null | undefined): string {
  return value ?? '';
}

function formatSupportedFieldPossibleValues(
  values: Array<{label: string; value: string}> | null | undefined,
): string {
  if (!values || values.length === 0) {
    return SUPPORTED_FIELD_NONE_VALUE;
  }

  return values.map((value) => value.label || value.value).join(', ');
}

function getSupportedFieldIgnorePossibleValues(
  metaData: Record<string, string> | null | undefined,
): boolean {
  if (!metaData) {
    return false;
  }

  const value =
    metaData.ignore_possible_values ?? metaData.ignorePossibleValues ?? '';

  return value.toLowerCase() === 'true';
}

function mapSupportedFieldApiToRow(
  field: DataAuditSupportedFieldApi,
): DataAuditSupportedFieldRow {
  return {
    id: field.id,
    senseEntityType: normalizeSupportedFieldText(field.senseEntityType),
    entityType: normalizeSupportedFieldText(field.entityType),
    atsEntityType: normalizeSupportedFieldText(field.atsEntityType),
    atsFieldName: normalizeSupportedFieldText(field.name),
    displayName: normalizeSupportedFieldText(field.displayName),
    possibleValues: formatSupportedFieldPossibleValues(field.possibleValues),
    overriddenPossibleValues: formatSupportedFieldPossibleValues(
      field.overriddenPossibleValues,
    ),
    ignorePossibleValues: getSupportedFieldIgnorePossibleValues(field.metaData),
  };
}

export function mapSupportedFieldsApiToRows(
  fields: Array<DataAuditSupportedFieldApi>,
): Array<DataAuditSupportedFieldRow> {
  return fields
    .filter((field) => !field.deleted)
    .map(mapSupportedFieldApiToRow);
}

function formatDataAuditSenseEntityTypeLabel(senseEntityType: string): string {
  return startCase(senseEntityType);
}

function findDataAuditEntityTypeForSupportedField(
  row: Pick<
    DataAuditSupportedFieldRow,
    'entityType' | 'senseEntityType' | 'atsEntityType'
  >,
  entityTypes: Array<DataAuditEntityType> | undefined,
): DataAuditEntityType | undefined {
  const types = entityTypes ?? [];

  if (row.entityType) {
    const byEntityType = types.find(
      (entityType) => entityType.entityType === row.entityType,
    );
    if (byEntityType) {
      return byEntityType;
    }
  }

  if (row.atsEntityType) {
    const byAtsEntityType = types.find(
      (entityType) => entityType.atsEntityType === row.atsEntityType,
    );
    if (byAtsEntityType) {
      return byAtsEntityType;
    }
  }

  if (row.senseEntityType) {
    const normalizedSenseEntityType = row.senseEntityType.toLowerCase();
    return types.find(
      (entityType) =>
        entityType.senseEntityType.toLowerCase() === normalizedSenseEntityType,
    );
  }

  return undefined;
}

function getDataAuditSenseEntityTypeKeyForSupportedField(
  row: Pick<
    DataAuditSupportedFieldRow,
    'entityType' | 'senseEntityType' | 'atsEntityType'
  >,
  entityTypes: Array<DataAuditEntityType> | undefined,
): string {
  const matchedEntityType = findDataAuditEntityTypeForSupportedField(
    row,
    entityTypes,
  );

  return matchedEntityType?.senseEntityType ?? row.senseEntityType;
}

export function getDataAuditSenseEntityTypeLabelForSupportedField(
  row: Pick<
    DataAuditSupportedFieldRow,
    'entityType' | 'senseEntityType' | 'atsEntityType'
  >,
  entityTypes: Array<DataAuditEntityType> | undefined,
): string {
  return formatDataAuditSenseEntityTypeLabel(
    getDataAuditSenseEntityTypeKeyForSupportedField(row, entityTypes),
  );
}

export function getDataAuditAtsEntityTypeKeyForSupportedField(
  row: Pick<
    DataAuditSupportedFieldRow,
    'entityType' | 'senseEntityType' | 'atsEntityType'
  >,
  entityTypes: Array<DataAuditEntityType> | undefined,
): string {
  const matchedEntityType = findDataAuditEntityTypeForSupportedField(
    row,
    entityTypes,
  );

  return matchedEntityType?.atsEntityType ?? row.atsEntityType;
}

export function getDataAuditAtsEntityTypeLabelForSupportedField(
  row: Pick<
    DataAuditSupportedFieldRow,
    'entityType' | 'senseEntityType' | 'atsEntityType'
  >,
  entityTypes: Array<DataAuditEntityType> | undefined,
): string {
  return getDataAuditAtsEntityTypeKeyForSupportedField(row, entityTypes);
}
